import React, { useState } from 'react'
import { doc, updateDoc } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { signOut } from 'firebase/auth'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { db, auth } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'
import { openBillingPortal, createCheckoutSession } from '../../utils/stripe'

const DEFAULT_WORK_AREAS = ['Substation', 'BESS', 'Collection', 'TLine']
const DEFAULT_CATEGORIES = ['Engineering', 'Execution', 'Project Quality', 'Safety']
const PLAN_LABELS: Record<string, string> = { core: 'Core', field: 'Field', premium: 'Premium', enterprise: 'Enterprise' }
const PLAN_PRICES: Record<string, string> = { field: '$49/mo', premium: '$149/mo', enterprise: '$299/mo' }
const NEXT_PLAN: Record<string, string | null> = { core: 'field', field: 'premium', premium: 'enterprise', enterprise: null }

export default function SettingsPage() {
  const { orgData, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [stripeMessage, setStripeMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [orgName, setOrgName] = useState(orgData?.name || '')
  const [workAreas, setWorkAreas] = useState<string[]>(orgData?.workAreas || DEFAULT_WORK_AREAS)
  const [categories, setCategories] = useState<string[]>(orgData?.categories || DEFAULT_CATEGORIES)
  const [newWorkArea, setNewWorkArea] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [saving, setSaving] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const success = params.get('stripe_success')
    const cancelled = params.get('stripe_cancel')
    const plan = params.get('plan')
    if (success) {
      setStripeMessage({ type: 'success', text: `Subscription activated! Welcome to ${PLAN_LABELS[plan || 'field'] || 'Field'}.` })
      window.history.replaceState({}, '', '/settings')
    } else if (cancelled) {
      setStripeMessage({ type: 'error', text: 'Checkout was cancelled. No charges were made.' })
      window.history.replaceState({}, '', '/settings')
    }
  }, [])

  const handleSave = async () => {
    if (!orgData?.id) return
    setSaving(true)
    try {
      await updateDoc(doc(db, 'organizations', orgData.id), {
        name: orgName,
        workAreas,
        categories,
        updatedAt: new Date().toISOString()
      })
    } catch (e) { console.error(e) }
    finally { setSaving(false) }
  }

  const addWorkArea = () => {
    if (!newWorkArea.trim()) return
    setWorkAreas(prev => [...prev, newWorkArea.trim()])
    setNewWorkArea('')
  }

  const removeWorkArea = (w: string) => setWorkAreas(prev => prev.filter(x => x !== w))
  const addCategory = () => {
    if (!newCategory.trim()) return
    setCategories(prev => [...prev, newCategory.trim()])
    setNewCategory('')
  }
  const removeCategory = (c: string) => setCategories(prev => prev.filter(x => x !== c))

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return
    setDeleting(true)
    setDeleteError('')
    try {
      const functions = getFunctions()
      const deleteAccountFn = httpsCallable(functions, 'deleteAccount')
      await deleteAccountFn()
      await signOut(auth)
      navigate('/login', { replace: true })
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete account. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const adminOnly = (
    <div className="max-w-2xl space-y-8">
      {/* Organization */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Organization</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
          <input value={orgName} onChange={e => setOrgName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-600 font-medium">{PLAN_LABELS[orgData?.plan || 'core'] || 'Core'}</span>
          {orgData?.plan && NEXT_PLAN[orgData.plan] && (
            <button onClick={() => createCheckoutSession(NEXT_PLAN[orgData.plan]!)} className="text-sm text-blue-600 hover:underline">
              Upgrade to {PLAN_LABELS[NEXT_PLAN[orgData.plan]!]} ({PLAN_PRICES[NEXT_PLAN[orgData.plan]!] || ''})
            </button>
          )}
          <button onClick={openBillingPortal} className="text-sm text-gray-500 hover:underline">Billing Portal →</button>
        </div>
      </div>

      {/* Work Areas */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Work Areas</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {workAreas.map(w => (
            <span key={w} className="flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full text-sm">
              {w}
              <button onClick={() => removeWorkArea(w)} className="text-gray-400 hover:text-red-500">×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newWorkArea} onChange={e => setNewWorkArea(e.target.value)} placeholder="New work area"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          <button onClick={addWorkArea} className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium">Add</button>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map(c => (
            <span key={c} className="flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full text-sm">
              {c}
              <button onClick={() => removeCategory(c)} className="text-gray-400 hover:text-red-500">×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="New category"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          <button onClick={addCategory} className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium">Add</button>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-300">
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  )

  return (
    <AppLayout title="Settings">
      {stripeMessage && (
        <div className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium ${
          stripeMessage.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {stripeMessage.text}
        </div>
      )}
      {isAdmin() && !orgData && (
        <div className="flex justify-center py-12 text-gray-400">Loading...</div>
      )}
      {isAdmin() && orgData && adminOnly}
      {!isAdmin() && (
        <p className="text-gray-400 text-center py-12">Admin access required.</p>
      )}

      {/* Account Deletion — available to all users */}
      <div className="max-w-2xl mt-8">
        <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-2">Danger Zone</h3>
          <p className="text-sm text-gray-500 mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
          >
            Delete Account
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Account</h2>
            <p className="text-sm text-gray-500 mb-6">
              This will permanently delete your account and remove all your data from SiteDeck, including your profile, lessons, timesheets, certifications, and all other associated records. <strong>This action cannot be undone.</strong>
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-mono bg-gray-100 px-1 rounded">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm mb-4"
            />

            {deleteError && (
              <p className="text-red-600 text-sm mb-4">{deleteError}</p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); setDeleteError('') }}
                disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:bg-red-300"
              >
                {deleting ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
